import React, { memo } from 'react';
import { Box, Button, ButtonBase, Paper, Skeleton, Typography, alpha } from '@mui/material';
import { Add as AddIcon, LibraryBooksOutlined, ChevronRightRounded } from '@mui/icons-material';
import EmptyState from '../common/EmptyState';
import { QuestionBank } from './types';
import { fmtClassName } from '../../utils/pdf/format';

interface BankOverviewProps {
  banks: QuestionBank[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  /** Narrowed to one class by the filter bar. */
  classFiltered: boolean;
  canWrite: boolean;
  onOpen: (bank: QuestionBank) => void;
  onCreate: () => void;
  onLoadMore: () => void;
}

/** A small, calm set of hues so each subject keeps a recognisable tile. */
const TILE_HUES = ['#2563eb', '#7c3aed', '#0d9488', '#d97706', '#db2777', '#0891b2', '#4f46e5', '#16a34a'];
const hueFor = (key: string): string => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TILE_HUES[h % TILE_HUES.length];
};

export const nameOf = (ref: QuestionBank['classId']): string => (typeof ref === 'string' ? '' : ref?.name || '');

const gridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
} as const;

const BankTile = memo<{ bank: QuestionBank; onOpen: (bank: QuestionBank) => void }>(({ bank, onOpen }) => {
  const subject = nameOf(bank.subjectId);
  const hue = hueFor(subject);
  const chapters = bank.chapterCount || 0;
  const questions = bank.totalQuestionCount || 0;
  return (
    <ButtonBase
      onClick={() => onOpen(bank)}
      sx={(theme) => ({
        display: 'block',
        textAlign: 'left',
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
        transition: 'border-color 150ms, box-shadow 150ms, transform 150ms',
        '&:hover': {
          borderColor: alpha(hue, 0.5),
          boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.07)}`,
          transform: 'translateY(-1px)',
        },
        '&:hover .tile-go': { opacity: 1, transform: 'translateX(0)' },
        '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${alpha(hue, 0.35)}` },
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 800,
            fontSize: '1rem',
            color: hue,
            bgcolor: alpha(hue, 0.1),
          }}
        >
          {subject.slice(0, 2).toUpperCase()}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>{subject}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{fmtClassName(nameOf(bank.classId))}</Typography>
        </Box>
        <ChevronRightRounded
          className="tile-go"
          sx={{ color: 'text.secondary', opacity: 0, transform: 'translateX(-4px)', transition: 'opacity 150ms, transform 150ms' }}
        />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', gap: 2.5 }}>
        <Stat value={chapters} label={chapters === 1 ? 'chapter' : 'chapters'} />
        <Stat value={questions} label={questions === 1 ? 'question' : 'questions'} />
      </Box>
    </ButtonBase>
  );
});
BankTile.displayName = 'BankTile';

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box>
    <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{value}</Typography>
    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.6 }}>{label}</Typography>
  </Box>
);

/** Every question bank the user can open, as tiles. The landing view of the Question Bank page. */
const BankOverview = memo<BankOverviewProps>(({
  banks, total, loading, loadingMore, classFiltered, canWrite, onOpen, onCreate, onLoadMore,
}) => {
  if (loading) {
    return (
      <Box sx={gridSx}>
        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rounded" height={118} sx={{ borderRadius: '14px' }} />)}
      </Box>
    );
  }

  if (banks.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: '14px', border: 'none' }}>
        <EmptyState
          icon={<LibraryBooksOutlined />}
          title={classFiltered ? 'No question banks for this class yet' : 'Build your first question bank'}
          description={classFiltered
            ? 'Pick a subject above to start one, or clear the class to see every bank.'
            : 'A question bank holds one subject for one class, organised into chapters. Papers are built from it in a few clicks.'}
          action={canWrite && !classFiltered ? { label: 'Create question bank', icon: <AddIcon />, onClick: onCreate } : undefined}
          sx={{ py: { xs: 6, md: 10 } }}
        />
      </Paper>
    );
  }

  return (
    <>
      <Box sx={gridSx}>
        {banks.map((bank) => <BankTile key={bank._id} bank={bank} onOpen={onOpen} />)}
        {canWrite && (
          <ButtonBase
            onClick={onCreate}
            sx={(theme) => ({
              minHeight: 118,
              borderRadius: '14px',
              border: '1px dashed',
              borderColor: 'divider',
              color: 'text.secondary',
              gap: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              transition: 'border-color 150ms, color 150ms, background-color 150ms',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.03) },
            })}
          >
            <AddIcon fontSize="small" /> New question bank
          </ButtonBase>
        )}
      </Box>
      {banks.length < total && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button onClick={onLoadMore} disabled={loadingMore} sx={{ textTransform: 'none' }}>
            {loadingMore ? 'Loading…' : `Show more (${total - banks.length} more)`}
          </Button>
        </Box>
      )}
    </>
  );
});

BankOverview.displayName = 'BankOverview';

export default BankOverview;
